import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Container, Typography, Card, CardContent, Button, CircularProgress, Alert,
  Chip, TextField, MenuItem, Divider,
} from '@mui/material';
import { useParams, useLocation } from 'react-router-dom';
import Navbar from '../../components/Navbar';
import RequireAuth from '../../components/athletes/RequireAuth';
import { useAuth } from '../../context/AuthContext';
import { getTeam, listTeamStaff, addTeamStaff, revokeTeamStaff } from '../../services/teamService';
import { hasProgramManagementAuthorityForTeam } from '../../services/programService';
import { roleLabel, sortRoles, toCoachingStaffOnlyByRows } from '../../utils/teamRoles';
import TeamHubLayout from '../../components/teams/TeamHubLayout';

// Team Admin is a higher-sensitivity Season-Team administration role,
// managed from Manage Team Settings (TeamSettingsPage), not this generic
// operational-staff page — never offered here as addable or removable,
// regardless of the caller's own role. add_team_staff()/revoke_team_
// staff() (009) are completely unchanged and still support team_admin;
// this is a UI placement decision only, not a backend/authorization
// change.
function canManageRole(myRoles, targetRole) {
  if (targetRole === 'team_admin') return false;
  if (myRoles.includes('team_admin')) return true;
  if (myRoles.includes('head_coach') && targetRole === 'assistant_coach') return true;
  return false;
}

function grantableRoles(myRoles) {
  if (myRoles.includes('team_admin')) return ['head_coach', 'assistant_coach'];
  if (myRoles.includes('head_coach')) return ['assistant_coach'];
  return [];
}

function AddStaffForm({ teamId, grantable, onAdded, initialRole }) {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState(() => (initialRole && grantable.includes(initialRole) ? initialRole : (grantable[0] || '')));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const handleAdd = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    if (!email || !role) { setError('Enter an email and choose a role.'); return; }
    setSaving(true);
    try {
      const result = await addTeamStaff({ teamId, email, role });
      setSuccess(`${result.email || email} added as ${roleLabel(role)}.`);
      setEmail('');
      await onAdded();
    } catch (e2) {
      setError(e2.message || 'Could not add staff member.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card sx={{ backgroundColor: 'rgba(10,20,34,0.8)', border: '1px solid #1e2a3a', mb: 3 }}>
      <CardContent>
        <Typography sx={{ fontWeight: 800, mb: 1.5 }}>Add Staff</Typography>
        <Box component="form" onSubmit={handleAdd} sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'flex-start' }}>
          <TextField
            label="Email" type="email" size="small" value={email}
            onChange={e => setEmail(e.target.value)}
            placeholder="coach@example.com"
            sx={{ flex: 1, minWidth: 220 }}
          />
          <TextField select label="Role" size="small" value={role} onChange={e => setRole(e.target.value)} sx={{ minWidth: 170 }}>
            {grantable.map(r => <MenuItem key={r} value={r}>{roleLabel(r)}</MenuItem>)}
          </TextField>
          <Button type="submit" variant="contained" color="primary" disabled={saving} sx={{ textTransform: 'none', fontWeight: 700, height: 40 }}>
            {saving ? <CircularProgress size={18} sx={{ color: '#050c16' }} /> : 'Add'}
          </Button>
        </Box>
        <Typography sx={{ color: '#4a6080', fontSize: '0.75rem', mt: 1 }}>
          They must already have a Football for Nerds account under this email.
        </Typography>
        {error && <Alert severity="error" sx={{ mt: 1.5 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mt: 1.5 }}>{success}</Alert>}
      </CardContent>
    </Card>
  );
}

function TeamStaffInner() {
  const { teamId } = useParams();
  const location = useLocation();
  const { currentUser } = useAuth();
  // Set by TeamDetailPage's post-creation "Assign Head Coach →" CTA
  // (never Team Admin — that role isn't offered on this page at all,
  // see grantableRoles() above) so the Add Staff form opens with Head
  // Coach already selected instead of whichever role happens first.
  const preselectRole = location.state?.preselectRole;
  const [team, setTeam] = useState(null);
  const [staff, setStaff] = useState([]);
  const [hasProgramAuthority, setHasProgramAuthority] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [revokingId, setRevokingId] = useState(null);
  const [revokeError, setRevokeError] = useState(null);

  const load = useCallback(async () => {
    const [t, s, hasAuthority] = await Promise.all([
      getTeam(teamId), listTeamStaff(teamId), hasProgramManagementAuthorityForTeam(teamId),
    ]);
    setTeam(t);
    setStaff(s);
    setHasProgramAuthority(hasAuthority);
  }, [teamId]);

  useEffect(() => {
    load().catch(e => setError(e.message)).finally(() => setLoading(false));
  }, [load]);

  const myRoles = useMemo(
    () => staff.filter(s => s.user_id === currentUser?.id && s.status === 'active').map(s => s.role),
    [staff, currentUser]
  );
  const grantable = grantableRoles(myRoles);

  const staffByPerson = useMemo(() => {
    const byUser = {};
    staff.filter(s => s.status === 'active').forEach(s => {
      if (!byUser[s.user_id]) byUser[s.user_id] = { userId: s.user_id, name: s.display_name || s.email, rows: [] };
      byUser[s.user_id].rows.push(s);
    });
    return Object.values(byUser).map(p => ({ ...p, rows: p.rows.sort((a, b) => sortRoles([a.role, b.role]).indexOf(a.role) - sortRoles([a.role, b.role]).indexOf(b.role)) }));
  }, [staff]);

  // A viewer holding no team_staff role at all and no Program authority
  // for this team is, in current Team Hub terms, a read-only connected
  // guardian (033) — never inferred as "guardian" from anything backend-
  // specific, just "not any kind of staff/management viewer," using the
  // same two authority signals this page already computes for itself.
  // Team Admin remains a real, fully unchanged team_staff role for
  // everyone — this only controls what a non-staff viewer is SHOWN.
  const isReadOnlyViewer = myRoles.length === 0 && !hasProgramAuthority;
  const displayedStaffByPerson = isReadOnlyViewer ? toCoachingStaffOnlyByRows(staffByPerson) : staffByPerson;

  const handleRevoke = async (row) => {
    setRevokeError(null);
    setRevokingId(row.id);
    try {
      await revokeTeamStaff(row.id);
      await load();
    } catch (e) {
      setRevokeError(e.message || 'Could not remove that staff member.');
    } finally {
      setRevokingId(null);
    }
  };

  if (loading) return <Box sx={{ py: 10, textAlign: 'center' }}><CircularProgress sx={{ color: '#2ea84b' }} /></Box>;
  if (error) return <Container maxWidth="sm" sx={{ py: 6 }}><Alert severity="error">{error}</Alert></Container>;
  if (!team) return null;

  return (
    <TeamHubLayout team={team} active="staff">
      {grantable.length > 0 && <AddStaffForm teamId={teamId} grantable={grantable} onAdded={load} initialRole={preselectRole} />}

      {revokeError && <Alert severity="error" sx={{ mb: 2 }}>{revokeError}</Alert>}

      <Card sx={{ backgroundColor: 'rgba(10,20,34,0.8)', border: '1px solid #1e2a3a' }}>
        <CardContent>
          <Typography sx={{ fontWeight: 800, mb: 1.5 }}>{isReadOnlyViewer ? 'Coaching Staff' : 'Staff'}</Typography>
          <Divider sx={{ mb: 1.5 }} />
          {displayedStaffByPerson.length === 0 ? (
            <Typography sx={{ color: '#6a8aa8' }}>{isReadOnlyViewer ? 'No coaching staff assigned yet.' : 'No active staff.'}</Typography>
          ) : (
            displayedStaffByPerson.map((person, idx) => (
              <Box key={person.userId}>
                {idx > 0 && <Divider sx={{ my: 1.5 }} />}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                  <Typography sx={{ fontWeight: 700 }}>{person.name}</Typography>
                  <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                    {person.rows.map(row => {
                      const removable = canManageRole(myRoles, row.role);
                      return (
                        <Chip
                          key={row.id}
                          label={roleLabel(row.role)}
                          onDelete={removable ? () => handleRevoke(row) : undefined}
                          disabled={revokingId === row.id}
                          title={row.role === 'team_admin' ? 'Manage Team Admin roles from Manage Team Settings' : undefined}
                          sx={{ bgcolor: '#0a2a14', color: '#2ea84b', fontWeight: 700 }}
                        />
                      );
                    })}
                  </Box>
                </Box>
              </Box>
            ))
          )}
        </CardContent>
      </Card>
    </TeamHubLayout>
  );
}

export default function TeamStaffPage() {
  return (
    <Box sx={{ minHeight: '100vh' }}>
      <Navbar />
      <RequireAuth><TeamStaffInner /></RequireAuth>
    </Box>
  );
}
